import React, { type FC, type ReactNode, type PropsWithChildren } from 'react';

import styled from '@emotion/styled';
import { isBrowser } from '@lucid/utils';
import { mqMin } from '@lucid/theme';

import Button from '../button/button';

export interface NavSkipButtonProps {
  /** The children of the Nav component. */
  children?: ReactNode;
  /** Allows the component to get additional styles. */
  className?: string;
  /** The label for the skip button. */
  label?: string;
}

const StyledNavSkipButton = styled(Button)(({ theme }) => ({
  label: 'navSkipButton',
  position: 'absolute',
  top: 0,
  transform: 'translateY(50%)',
  left: '-100vw',
  margin: 'auto 0',
  zIndex: theme.zIndices.zMax,
  transition: 'left 0.25s ease-in-out',
  '&:active, &:focus': {
    left: theme.space.s16,
  },
  [mqMin.tabletLandscape]: {
    left: '-50vw',
  },
}));

const NavSkipButton: FC<PropsWithChildren<NavSkipButtonProps>> = ({ children, label = 'Skip to Content', ...rest }) => {
  const onClickSkipHandler = () => {
    if (isBrowser()) {
      const [pageBodyRoot] = document.getElementsByClassName('gatsby-root');
      const firstFocusElement = pageBodyRoot.querySelector(
        ':is(h1, h2, h3, h4, h5, h6, a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])):not(nav *, [data-skip="true"])',
      ) as HTMLElement;

      if (firstFocusElement) {
        const isHeading = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(firstFocusElement.tagName);
        firstFocusElement.setAttribute('tabindex', isHeading ? '-1' : '0');
        firstFocusElement.focus();
      }
    }
  };

  return (
    <StyledNavSkipButton {...rest} data-skip onClick={onClickSkipHandler}>
      {children || label}
    </StyledNavSkipButton>
  );
};

export { NavSkipButton };
export default NavSkipButton;
